import type React from "react";

import { Box } from "../../../components/ui/box";
import { Separator } from "../../../components/ui/separator";
import { DELAYED_FADE, Skeleton } from "../../../components/ui/skeleton";
import { useElementSize } from "../../../hooks/use-element-size";
import type { Omittable } from "../../../lib/props";
import type { WidgetSkeletonVariant } from "./widget-body-shapes";
import { useWidgetBodyLoadingWhile } from "../contexts";
import { CARET_PX, ROW_GAP } from "../widget-disclosure-row";
import { LIST_ROW_PX } from "../widget-list-row";

export type { WidgetSkeletonVariant } from "./widget-body-shapes";

export type WidgetSkeletonProps = Omittable<{
  variant?: WidgetSkeletonVariant;
  /**
   * `list` / `activity` / `bars`: the row count. Left off, the skeleton
   * measures its body and lays as many rows as the settled list would fit,
   * so the placeholder is the height of the content that replaces it.
   */
  rows?: number;
  /**
   * `list`: the widths of the trailing figure columns, in row order — the
   * same widths the settled rows hand `WidgetListHeader`, so a figure bar
   * sits exactly under the column that names it.
   */
  figures?: readonly number[];
  /** `list`: the glyph seat every row leads with. A `circle` is a person; a
   *  `tile` is an icon-kind entity. */
  leading?: "circle" | "tile";
  /** `list`: the rows below lead with a disclosure caret, so the placeholder
   *  holds that slot open the way {@link WidgetDisclosureRowSkeleton} does. */
  caretSlot?: boolean;
}>;

/** Bar lengths of a ranked horizontal chart, longest first. */
const BAR_LENGTHS = ["92%", "74%", "61%", "50%", "38%", "29%", "21%", "14%"] as const;

/** Column heights of a vertical chart — a plausible series, one peak. */
const COLUMN_HEIGHTS = ["42%", "66%", "31%", "88%", "54%", "72%", "46%", "60%", "35%", "78%", "50%", "64%"] as const;

/** A time series drawn on a 100×40 plane, stretched to the plot. */
const LINE_PATH = "M0 30 L12 24 L24 27 L36 17 L48 21 L60 12 L72 15 L84 8 L100 11";

/** Width of the value-axis gutter a `columns` / `line` chart holds. */
const VALUE_AXIS_PX = 36;

/** Width of the category-axis gutter a `bars` chart holds. */
const CATEGORY_AXIS_PX = 72;

/** The pitch one category row takes in a `bars` chart. */
const BAR_ROW_PX = 32;

/** The thickest bar a categorical chart draws. */
const BAR_MAX_PX = 48;

/** The widest a donut chart grows inside a card. */
const DONUT_MAX_PX = 360;

/** The pitch of a `line` / `columns` chart's category tick row. */
const TICK_ROW_PX = 20;

/** The big-number silhouette: the figure's cap height and the caption line under it. */
export const STAT_FIGURE_PX = 36;
export const STAT_CAPTION_PX = 10;

function fitRows(height: number, pitch: number, min: number, max: number, fallback: number): number {
  if (height <= 0) return fallback;
  return Math.max(min, Math.min(max, Math.floor(height / pitch)));
}

/**
 * The one loading state every widget body uses. A variant per body shape, so
 * a loading widget silhouettes what it is about to render — a board of loading
 * cards reads as a board of DIFFERENT widgets waiting on data, not a column
 * of identical grey lines. Anonymous by rule: bars in the settled rhythm,
 * never a label the body cannot promise. The skeleton fills the body and
 * measures it, so a list lays as many rows as will fit and a chart's plot is
 * the size the chart will be; content then lands with no reflow.
 *
 * Delayed as one group: invisible for the first ~200ms, then the whole
 * scaffold fades in together, so a warm cache or a fast query swaps content
 * in with no placeholder ever shown. Loading only — a data error still
 * `throw`s to the harness ErrorBoundary.
 */
export function WidgetSkeleton({
  variant = "list",
  rows,
  figures,
  leading = "tile",
  caretSlot = false,
}: WidgetSkeletonProps) {
  // Tell the measuring card its body is a placeholder while this is mounted,
  // so autoHeight commits settle the grid without the FLIP spring (loading
  // heights are not user actions).
  useWidgetBodyLoadingWhile(true);
  const { ref, size } = useElementSize<HTMLDivElement>();

  let body: React.ReactNode;
  switch (variant) {
    case "stat":
      body = <StatShape />;
      break;
    case "band":
      body = <BandShape />;
      break;
    case "bars":
      body = <BarsShape height={size.height} rows={rows ?? fitRows(size.height, BAR_ROW_PX, 3, BAR_LENGTHS.length, 5)} />;
      break;
    case "columns":
      body = <ColumnsShape width={size.width} />;
      break;
    case "line":
      body = <LineShape width={size.width} />;
      break;
    case "donut":
      body = <DonutShape height={size.height} width={size.width} />;
      break;
    case "battery":
      body = <BatteryShape />;
      break;
    case "treemap":
      body = <TreemapShape />;
      break;
    case "activity":
      body = <ActivityShape rows={rows ?? fitRows(size.height, LIST_ROW_PX, 2, 12, 3)} />;
      break;
    default:
      body = (
        <ListShape
          caretSlot={caretSlot}
          figures={figures}
          leading={leading}
          rows={rows ?? fitRows(size.height, LIST_ROW_PX, 3, 16, 4)}
        />
      );
  }

  return (
    <Box
      ref={ref}
      aria-hidden
      className={DELAYED_FADE}
      data-slot="widget-skeleton"
      data-variant={variant}
      direction="column"
      fill
      overflow="hidden"
    >
      {body}
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Lists
 * ------------------------------------------------------------------ */

function ListShape({
  rows,
  figures,
  leading,
  caretSlot,
}: {
  rows: number;
  figures: readonly number[] | undefined;
  leading: "circle" | "tile";
  caretSlot: boolean;
}) {
  return (
    <Box direction="column">
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} align="center" direction="row" gap={ROW_GAP} height={LIST_ROW_PX}>
          {caretSlot ? <Box shrink={false} width={CARET_PX} /> : null}
          {leading === "circle" ? (
            <Skeleton circle width={20} />
          ) : (
            <Skeleton className="rounded-[4px]" height={16} width={16} />
          )}
          <Box fill="flex" minWidth={0}>
            <Skeleton height={12} vary={index} width={148} />
          </Box>
          {figures === undefined ? (
            <Skeleton height={12} width={40} />
          ) : (
            figures.map((width, column) => (
              <Box key={column} align="end" shrink={false} width={width}>
                <Skeleton height={12} width={Math.min(width, 28 + ((index + column) % 3) * 8)} />
              </Box>
            ))
          )}
        </Box>
      ))}
    </Box>
  );
}

function ActivityShape({ rows }: { rows: number }) {
  // One sentence per row beside a 20px glyph, the time lifted out to the
  // row's end — the feed's own truncated line.
  return (
    <Box direction="column">
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} align="center" direction="row" gap="sm" height={LIST_ROW_PX}>
          <Skeleton circle width={20} />
          <Box fill="flex" minWidth={0}>
            <Skeleton height={12} width={`${76 - (index % 3) * 14}%`} />
          </Box>
          <Skeleton height={12} width={40} />
        </Box>
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Figures
 * ------------------------------------------------------------------ */

function StatShape() {
  // The 4xl figure's line box over its muted caption, centered the way the
  // big-number bodies center theirs.
  return (
    <Box align="center" direction="column" fill gap="sm" justify="center">
      <Skeleton height={STAT_FIGURE_PX} width={96} />
      <Skeleton height={STAT_CAPTION_PX} width={72} />
    </Box>
  );
}

function BandShape() {
  // Three figure columns between hairlines — the `StatBand` row at rest.
  return (
    <Box align="center" direction="row" fill gap="sm" minHeight={60}>
      {[0, 1, 2].map((column) => (
        <Box key={column} align="center" direction="row" fill="flex" gap="sm" minWidth={0}>
          {column > 0 ? (
            <Box alignSelf="stretch" direction="row" marginY="xs" shrink={false}>
              <Separator orientation="vertical" />
            </Box>
          ) : null}
          <Box direction="column" fill="flex" gap="sm" minWidth={0} paddingX="sm">
            <Skeleton height={10} width="48%" />
            <Skeleton height={24} width="64%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Charts
 * ------------------------------------------------------------------ */

function BarsShape({ height, rows }: { height: number; rows: number }) {
  // Category labels in a left gutter, bars longest first — the `rows`
  // orientation of a categorical bar chart. The bar takes half the pitch
  // its row lands on, between a text line and the chart's 48px cap.
  const pitch = height > 0 ? height / rows : BAR_ROW_PX;
  const thickness = Math.round(Math.min(BAR_MAX_PX, Math.max(12, pitch * 0.5)));
  return (
    <Box direction="column" fill justify="evenly">
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} align="center" direction="row" gap="sm">
          <Box align="end" shrink={false} width={CATEGORY_AXIS_PX}>
            <Skeleton height={10} width={32 + ((index * 7) % 3) * 12} />
          </Box>
          <Skeleton height={thickness} width={BAR_LENGTHS[index % BAR_LENGTHS.length]} />
        </Box>
      ))}
    </Box>
  );
}

function ValueAxis() {
  return (
    <Box direction="column" justify="between" paddingY="xs" shrink={false} width={VALUE_AXIS_PX}>
      {[0, 1, 2, 3].map((tick) => (
        <Skeleton key={tick} height={8} width={20} />
      ))}
    </Box>
  );
}

function CategoryTicks({ count }: { count: number }) {
  return (
    <Box align="start" direction="row" height={TICK_ROW_PX} justify="between" paddingTop="xs">
      {Array.from({ length: count }, (_, tick) => (
        <Skeleton key={tick} height={8} width={24} />
      ))}
    </Box>
  );
}

function ColumnsShape({ width }: { width: number }) {
  const count = width <= 0 ? 6 : Math.max(4, Math.min(COLUMN_HEIGHTS.length, Math.floor(width / 48)));
  return (
    <Box direction="row" fill gap="sm">
      <ValueAxis />
      <Box direction="column" fill="flex" minWidth={0}>
        <Box align="end" border="bottom" direction="row" fill="flex" gap="sm" minHeight={0} paddingX="xs">
          {Array.from({ length: count }, (_, index) => (
            <Box key={index} align="end" fill="flex" minWidth={0}>
              <Skeleton
                className="max-w-12 rounded-b-none rounded-t-[3px]"
                fillWidth
                height={COLUMN_HEIGHTS[index % COLUMN_HEIGHTS.length]}
              />
            </Box>
          ))}
        </Box>
        <CategoryTicks count={count} />
      </Box>
    </Box>
  );
}

function LineShape({ width }: { width: number }) {
  const ticks = width <= 0 ? 5 : Math.max(3, Math.min(8, Math.floor(width / 96)));
  return (
    <Box direction="row" fill gap="sm">
      <ValueAxis />
      <Box direction="column" fill="flex" minWidth={0}>
        <Box border="bottom" fill="flex" minHeight={0} paddingTop="xs">
          {/* The series is one wash the plot stretches to its box; the
              stroke keeps its own width under that stretch. */}
          <svg
            className="size-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 100 40"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d={`${LINE_PATH} L100 40 L0 40 Z`} fill="var(--wash-skeleton)" />
            <path
              d={LINE_PATH}
              stroke="var(--border)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </Box>
        <CategoryTicks count={ticks} />
      </Box>
    </Box>
  );
}

function Legend({ items }: { items: number }) {
  return (
    <Box align="center" direction="row" gap="lg" justify="center" shrink={false} wrap>
      {Array.from({ length: items }, (_, index) => (
        <Box key={index} align="center" direction="row" gap="xs">
          <Skeleton circle width={8} />
          <Skeleton height={10} width={40 + (index % 2) * 16} />
        </Box>
      ))}
    </Box>
  );
}

function DonutShape({ height, width }: { height: number; width: number }) {
  // The ring takes the height the legend leaves it, capped where the chart
  // caps itself and floored where a ring stops reading as one; the band is
  // the chart's own share of the radius (a 0.68 inner ratio).
  const legendPx = 24 + 8;
  const diameter = Math.max(
    48,
    Math.min(DONUT_MAX_PX, height > 0 ? height - legendPx : DONUT_MAX_PX, width > 0 ? width : DONUT_MAX_PX),
  );
  return (
    <Box align="center" direction="column" fill gap="sm" justify="center">
      <Skeleton
        circle
        className="[mask:radial-gradient(farthest-side,transparent_67%,#000_68%)]"
        width={diameter}
      />
      <Legend items={3} />
    </Box>
  );
}

function BatteryShape() {
  // One 48px stacked bar in three segments over its legend — a proportion bar.
  return (
    <Box direction="column" fill gap="sm" justify="center">
      <Box className="gap-[3px]" direction="row" height={48}>
        {["48%", "32%", "20%"].map((segment, index) => (
          <Skeleton key={index} className="rounded-lg" height={48} width={segment} />
        ))}
      </Box>
      <Legend items={3} />
    </Box>
  );
}

function TreemapShape() {
  // One lead tile beside a column of smaller ones, edge to edge.
  return (
    <Box className="gap-[4px]" direction="row" fill>
      <Skeleton className="flex-[3] min-h-0 min-w-0 rounded-[3px]" />
      <Box className="gap-[4px]" direction="column" flex={2} minWidth={0}>
        <Skeleton className="flex-[3] min-h-0 min-w-0 rounded-[3px]" />
        <Box className="gap-[4px]" direction="row" flex={2} minHeight={0}>
          <Skeleton className="flex-1 min-h-0 min-w-0 rounded-[3px]" />
          <Skeleton className="flex-1 min-h-0 min-w-0 rounded-[3px]" />
        </Box>
      </Box>
    </Box>
  );
}
